import type {
  DryRunUpstreamErrorPolicyRuleRequest,
  DryRunUpstreamErrorPolicyRuleResponse,
  ListUpstreamErrorObservationsResponse,
  ListUpstreamErrorPolicyRulesResponse,
  UpstreamErrorObservation,
  UpstreamErrorPolicyRule,
  UpstreamErrorPolicyRuleInput,
  UpstreamErrorPolicyRuleResponse,
  UpstreamErrorReviewStatus,
} from '@nadoutong/contracts';
import { opsApiRequest } from './client.ts';

const BASE = '/v1/ops/upstream-error-policy';

export interface ObservationFilters {
  readonly reviewStatus?: UpstreamErrorReviewStatus | 'all';
  readonly limit?: number;
}

export async function listUpstreamErrorObservations(
  filters: ObservationFilters,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<UpstreamErrorObservation[]> {
  const qs = new URLSearchParams();
  if (filters.reviewStatus !== undefined) {
    qs.set('reviewStatus', filters.reviewStatus);
  }
  if (filters.limit !== undefined) {
    qs.set('limit', String(filters.limit));
  }
  const suffix = qs.size > 0 ? `?${qs.toString()}` : '';
  const response = await opsApiRequest<ListUpstreamErrorObservationsResponse>(
    `${BASE}/observations${suffix}`,
    { signal },
    fetchImpl,
  );
  return [...response.observations];
}

export async function listUpstreamErrorPolicyRules(
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<UpstreamErrorPolicyRule[]> {
  const response = await opsApiRequest<ListUpstreamErrorPolicyRulesResponse>(
    `${BASE}/rules`,
    { signal },
    fetchImpl,
  );
  return [...response.rules];
}

export async function dryRunUpstreamErrorPolicyRule(
  body: DryRunUpstreamErrorPolicyRuleRequest,
  fetchImpl: typeof fetch,
): Promise<DryRunUpstreamErrorPolicyRuleResponse> {
  return opsApiRequest<DryRunUpstreamErrorPolicyRuleResponse>(
    `${BASE}/rules/dry-run`,
    { method: 'POST', body },
    fetchImpl,
  );
}

export async function createUpstreamErrorPolicyRule(
  body: UpstreamErrorPolicyRuleInput,
  fetchImpl: typeof fetch,
): Promise<UpstreamErrorPolicyRule> {
  const response = await opsApiRequest<UpstreamErrorPolicyRuleResponse>(
    `${BASE}/rules`,
    { method: 'POST', body },
    fetchImpl,
  );
  return response.rule;
}

export async function updateUpstreamErrorPolicyRule(
  id: string,
  body: UpstreamErrorPolicyRuleInput,
  fetchImpl: typeof fetch,
): Promise<UpstreamErrorPolicyRule> {
  const response = await opsApiRequest<UpstreamErrorPolicyRuleResponse>(
    `${BASE}/rules/${encodeURIComponent(id)}`,
    { method: 'PATCH', body },
    fetchImpl,
  );
  return response.rule;
}

export async function ignoreUpstreamErrorObservation(
  fingerprint: string,
  fetchImpl: typeof fetch,
): Promise<UpstreamErrorObservation> {
  const response = await opsApiRequest<{ observation: UpstreamErrorObservation }>(
    `${BASE}/observations/${encodeURIComponent(fingerprint)}/ignore`,
    { method: 'POST' },
    fetchImpl,
  );
  return response.observation;
}
